// VOY-485 (rebuild) — Recent searches (Public profile tab).
//
// Mirrors the live `RecentSearchesRow` data path: up to 3 recent saved
// searches, click-to-rerun via sessionStorage handoff to `/` (the unified
// search route). The live component already implements the re-run logic;
// we read the same `saved_searches` rows + reproduce the same handoff so
// the user's click lands them on a pre-filled search exactly as before.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plane, Hotel, Search as SearchIcon } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { SavedSearch } from '@/types/profile'

export interface RecentSearchesSectionProps {
  userId: string
}

function iconFor(type: SavedSearch['search_type']) {
  if (type === 'flight') return <Plane aria-hidden="true" />
  if (type === 'hotel') return <Hotel aria-hidden="true" />
  return <SearchIcon aria-hidden="true" />
}

function summary(s: SavedSearch): string {
  const dest = s.destination_city_name || s.query?.destination
  const origin = s.origin_city_name || s.query?.origin
  if (origin && dest) return `${origin} → ${dest}`
  return dest || origin || 'Search'
}

function dateLabel(s: SavedSearch): string {
  const dep = s.query?.departureDate || s.query?.checkIn
  if (!dep) return ''
  try {
    return new Date(String(dep)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

export function RecentSearchesSection({ userId }: RecentSearchesSectionProps) {
  const navigate = useNavigate()
  const [searches, setSearches] = useState<SavedSearch[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    void (async () => {
      setLoading(true)
      try {
        const { data, error } = await supabase
          .from('saved_searches')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(6)
        if (error) {
          console.error('[RecentSearchesSection] load failed:', error)
          setSearches([])
        } else {
          setSearches((data as SavedSearch[]) || [])
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [userId])

  function rerun(s: SavedSearch) {
    // Reproduce the live RecentSearchesRow handoff — pass the saved
    // structured query to the unified search route via sessionStorage so
    // the page boots into the right initial state.
    try {
      sessionStorage.setItem('voySearchData', JSON.stringify({ source: 'recent-search', search: s }))
    } catch (err) {
      console.warn('[RecentSearchesSection] sessionStorage handoff failed:', err)
    }
    navigate('/')
  }

  return (
    <section className="card" aria-labelledby="searches-h">
      <div className="card-h">
        <h3 id="searches-h">Recent searches</h3>
        <span className="meta">{loading ? 'LOADING…' : 'CLICK TO RE-RUN'}</span>
      </div>
      {!loading && searches.length === 0 ? (
        <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
          No recent searches.
        </p>
      ) : (
        <ul className="recent-list">
          {searches.slice(0, 6).map((s) => (
            <li key={s.id}>
              <button type="button" className="recent-row" onClick={() => rerun(s)}>
                <span className="recent-icon">{iconFor(s.search_type)}</span>
                <span className="recent-summary">{summary(s)}</span>
                <span className="recent-date">{dateLabel(s)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
